import { CANVAS_WIDTH } from "../../game/constants";
import { getCharacterById } from "../../economy/characters";
import { isMobileViewport } from "../../game/platform";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import {
  GAME_FONT,
  formatAccountLevel,
  formatDerivedAccountLevel,
  mFont,
  mFontPlain,
  menuButtonColumns,
  menuBtnH,
  menuGap,
  roundedRectPath,
  MENU_TEXT_SECONDARY,
  resolveMenuSafeTopOffset,
  uiCloseSize,
  UI_EDGE_PAD,
  UI_CLOSE_Y,
} from "../renderConstants";

// ── Types ────────────────────────────────────────────────────────────────

export interface VersusLobbyDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  nowSec: () => number;
  rafsbyPortrait: HTMLImageElement | null;
  uiRefreshIcon: HTMLImageElement | null;
  menuSafeAreaTop: number;
  /** Helper callbacks forwarded from the renderer class. */
  drawCharacterPortrait: (path: string, x: number, y: number, w: number, h: number) => void;
  drawCircleCloseButton: (rect: Rect) => void;
  drawHourglass: (cx: number, cy: number, size: number, color: string) => void;
}

// ── Extracted screen function ────────────────────────────────────────────

export function drawVersusLobbyScreen(deps: VersusLobbyDeps, model: RenderModel): UiRects {
  const ctx = deps.ctx;
  const centerX = CANVAS_WIDTH / 2;
  const menuCols = menuButtonColumns(centerX);
  const lobby = model.versusLobby;
  const menuTopOffset = resolveMenuSafeTopOffset(deps.menuSafeAreaTop, isMobileViewport());

  // Close button top-right on all lobby views
  const lobbyCloseRect: Rect = {
    x: CANVAS_WIDTH - uiCloseSize() - UI_EDGE_PAD,
    y: UI_CLOSE_Y + menuTopOffset,
    w: uiCloseSize(),
    h: uiCloseSize()
  };
  // Deferred draw — called before each return
  const drawLobbyClose = (rects: UiRects): UiRects => {
    deps.drawCircleCloseButton(lobbyCloseRect);
    rects.versusClose = lobbyCloseRect;
    return rects;
  };

  // Searching state — used for quickmatch flow (battle button) and countdown
  if (model.versusBusy || model.phase === "VERSUS_COUNTDOWN") {
    const ch = deps.canvasHeight;
    const char = model.equippedCharacter;
    const bigSize = CANVAS_WIDTH * 1.2;
    const timeSec = deps.nowSec();
    const bobAmp = 4;
    const bobOffset = Math.sin(timeSec * 1.8) * bobAmp;

    // Local player — left half, bobbing
    const localX = -bigSize / 2 + 35;
    const localY = ch - bigSize * 1.0 + bobOffset;
    deps.drawCharacterPortrait(char.imagePath, localX, localY, bigSize, bigSize);

    // Player name + level above portrait (drawn after so it's on top)
    const nameY = localY - 4;
    const nameXLocal = localX + bigSize / 2 + 20;
    ctx.fillStyle = "#d4e7ff";
    ctx.font = mFont("bold", 8);
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText((model.username ?? "PLAYER").toUpperCase(), nameXLocal, nameY);
    if (model.level > 0) {
      ctx.fillStyle = "#ffd700";
      ctx.font = mFont("bold", 6);
      ctx.textBaseline = "top";
      ctx.fillText(formatAccountLevel(model.level, model.prestigeStars), nameXLocal, nameY + 2);
    }

    // Opponent — right half, mirrored position, bobbing counter-phase
    const cd = model.versusCountdown;
    const opBob = Math.sin(timeSec * 1.8 + Math.PI) * bobAmp;
    const opX = CANVAS_WIDTH - (-bigSize / 2 + 35) - bigSize;
    const opY = ch - bigSize * 1.0 + opBob;

    if (cd?.opponentSkin != null) {
      // Opponent found — show their actual skin (flipped)
      const opChar = getCharacterById(cd.opponentSkin) ?? char;
      ctx.save();
      ctx.translate(CANVAS_WIDTH, 0);
      ctx.scale(-1, 1);
      if (cd.opponentSkin === "rafsby" && deps.rafsbyPortrait) {
        ctx.drawImage(deps.rafsbyPortrait, CANVAS_WIDTH - opX - bigSize, opY, bigSize, bigSize);
      } else {
        deps.drawCharacterPortrait(opChar.imagePath, CANVAS_WIDTH - opX - bigSize, opY, bigSize, bigSize);
      }
      ctx.restore();

      // Opponent name + level above portrait
      const opNameY = opY - 4;
      const nameXOp = opX + bigSize / 2 - 20;
      ctx.fillStyle = "#ffffff";
      ctx.font = mFont("bold", 8);
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillText((cd.opponentUsername || "OPPONENT").toUpperCase().slice(0, 10), nameXOp, opNameY);
      ctx.fillStyle = "#ffd700";
      ctx.font = mFont("bold", 6);
      ctx.textBaseline = "top";
      ctx.fillText(formatDerivedAccountLevel(cd.opponentLevel), nameXOp, opNameY + 2);
    } else {
      // Still searching — hourglass on opponent side (no silhouette)
      deps.drawHourglass(opX + bigSize / 2, opY + bigSize * 0.3, 48, "#ff9a9a");
    }

    // VS divider — between the skins
    ctx.fillStyle = "#ff6b6b";
    ctx.font = `bold 14px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(255,107,107,0.5)";
    ctx.shadowBlur = 10;
    ctx.fillText("VS", centerX, ch * 0.55);
    ctx.shadowBlur = 0;

    // Status line if present
    if (model.versusStatusLine) {
      ctx.fillStyle = "#ff9f6b";
      ctx.font = mFontPlain(5);
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(model.versusStatusLine.slice(0, 44), centerX, 156);
    }

    return drawLobbyClose({});
  }

  // Lobby browser (manual lobby button)
  if (lobby == null) {
    const gap = menuGap();
    const panelFill = "rgba(20,36,54,0.85)";
    const panelStroke = "rgba(108,200,255,0.6)";

    let curY = 150;

    // ── Lobby board header: OPEN LOBBIES  ...  Xm ago  [refresh] ──
    const listX = menuCols.fullX;
    const listW = menuCols.fullW;
    const listH = 180;
    const headerY = curY;
    const headerMidY = headerY + 7; // vertical center of header row

    ctx.fillStyle = "#6a8ab8";
    ctx.font = mFont("bold", 5);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText("OPEN LOBBIES", listX + 8, headerMidY);

    // Refresh icon (right-aligned)
    const refreshSize = 14;
    const versusRefreshLobbies: Rect = { x: listX + listW - refreshSize - 4, y: headerMidY - refreshSize / 2, w: refreshSize, h: refreshSize };
    if (deps.uiRefreshIcon) {
      ctx.drawImage(deps.uiRefreshIcon, versusRefreshLobbies.x, versusRefreshLobbies.y, refreshSize, refreshSize);
    }


    curY += 14;

    // Lobby list box
    roundedRectPath(ctx, listX, curY, listW, listH, 8);
    ctx.fillStyle = "rgba(12,19,36,0.85)";
    ctx.fill();
    roundedRectPath(ctx, listX, curY, listW, listH, 8);
    ctx.strokeStyle = "rgba(92,140,212,0.5)";
    ctx.lineWidth = 1;
    ctx.stroke();

    const rows = model.openLobbies.slice(0, 7);
    const versusLobbyRows: Rect[] = [];
    if (rows.length === 0) {
      ctx.fillStyle = MENU_TEXT_SECONDARY;
      ctx.font = mFontPlain(5);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(model.lobbyBrowserError || "NO OPEN LOBBIES", centerX, curY + listH / 2);
    } else {
      const lobbyRowH = isMobileViewport() ? 22 : 18;
      for (let i = 0; i < rows.length; i += 1) {
        const rowY = curY + 8 + i * (lobbyRowH + 3);
        const rowRect: Rect = { x: listX + 6, y: rowY, w: listW - 12, h: lobbyRowH };
        versusLobbyRows.push(rowRect);
        roundedRectPath(ctx, rowRect.x, rowRect.y, rowRect.w, rowRect.h, 4);
        ctx.fillStyle = i % 2 === 0 ? "rgba(26,41,70,0.6)" : "rgba(20,33,56,0.6)";
        ctx.fill();
        ctx.strokeStyle = "rgba(105,230,255,0.25)";
        ctx.stroke();
        ctx.fillStyle = "#d4e7ff";
        ctx.font = mFont("bold", 6);
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(rows[i].code, listX + 16, rowY + lobbyRowH / 2);
        ctx.textAlign = "right";
        ctx.fillStyle = "#9cc2ff";
        ctx.fillText(`${rows[i].players}/2`, listX + listW - 16, rowY + lobbyRowH / 2);
      }
    }
    ctx.textAlign = "center";

    curY += listH + gap;

    // Feature lock message
    if (!model.featureLocks.customLobbies.unlocked) {
      ctx.fillStyle = "#ffd1d1";
      ctx.font = mFont("bold", 5);
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(`UNLOCKS AT LV ${model.featureLocks.customLobbies.requiredLevel}`, centerX, curY);
      curY += 16;
    }

    // ── JOIN / CREATE buttons OR join input ──
    if (model.lobbyJoinInputOpen) {
      const inputH = menuBtnH() + 4;
      const inputGap = gap + 4;
      const goW = inputH + 8;
      const inputW = menuCols.fullW - goW - inputGap;
      const codeInputRect: Rect = { x: menuCols.fullX, y: curY, w: inputW, h: inputH };
      roundedRectPath(ctx, codeInputRect.x, codeInputRect.y, codeInputRect.w, codeInputRect.h, 6);
      ctx.fillStyle = panelFill;
      ctx.fill();
      ctx.strokeStyle = panelStroke;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      const versusJoinLobby: Rect = { x: menuCols.fullX + inputW + inputGap, y: curY, w: goW, h: inputH };
      roundedRectPath(ctx, versusJoinLobby.x, versusJoinLobby.y, versusJoinLobby.w, versusJoinLobby.h, 6);
      ctx.fillStyle = panelFill;
      ctx.fill();
      ctx.strokeStyle = panelStroke;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = "#e9f7ff";
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("GO", versusJoinLobby.x + versusJoinLobby.w / 2, versusJoinLobby.y + versusJoinLobby.h / 2 + 1);

      curY += inputH + gap + 4;
      const backW = 72;
      const backH = 22;
      const lobbyToggleJoin: Rect = { x: centerX - backW / 2, y: curY, w: backW, h: backH };
      roundedRectPath(ctx, lobbyToggleJoin.x, lobbyToggleJoin.y, lobbyToggleJoin.w, lobbyToggleJoin.h, 6);
      ctx.fillStyle = "rgba(20,36,54,0.6)";
      ctx.fill();
      ctx.strokeStyle = "rgba(108,200,255,0.3)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "#6a8ab8";
      ctx.font = mFont("bold", 5);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("< BACK", centerX, curY + backH / 2);

      return drawLobbyClose({
        lobbyCodeInput: codeInputRect,
        versusJoinLobby,
        lobbyToggleJoin,
        versusRefreshLobbies,
        versusLobbyRows
      });
    }

    // Default: JOIN / CREATE buttons (clean style matching lobby page)
    const lobbyToggleJoin: Rect = { x: menuCols.leftX, y: curY, w: menuCols.halfW, h: menuBtnH() };
    const versusCreateLobby: Rect = { x: menuCols.rightX, y: curY, w: menuCols.halfW, h: menuBtnH() };
    for (const [rect, label] of [[lobbyToggleJoin, "JOIN"], [versusCreateLobby, "CREATE"]] as const) {
      roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
      ctx.fillStyle = panelFill;
      ctx.fill();
      ctx.strokeStyle = panelStroke;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = "#e9f7ff";
      ctx.font = mFont("bold", 5);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1);
    }

    return drawLobbyClose({
      lobbyToggleJoin,
      versusCreateLobby,
      versusRefreshLobbies,
      versusLobbyRows
    });
  }

  // ── Active lobby: battle-style layout with big character portraits ──
  if (lobby != null) {
    const ch = deps.canvasHeight;
    const char = model.equippedCharacter;
    const bigSize = CANVAS_WIDTH * 1.2;
    const timeSec = deps.nowSec();
    const bobAmp = 4;

    const localPlayer = lobby.players.find(p => p.isLocal);
    const opponent = lobby.players.find(p => !p.isLocal);

    // Shared style matching X close button: fill rgba(20,36,54,0.85), stroke rgba(108,200,255,0.6)
    const panelFill = "rgba(20,36,54,0.85)";
    const panelStroke = "rgba(108,200,255,0.6)";

    // ── LOBBY title ──
    let topY = 50 + menuTopOffset;
    ctx.fillStyle = "#d4e7ff";
    ctx.font = `bold 12px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("LOBBY", centerX, topY);
    topY += 20;

    // ── Lobby code ──
    ctx.fillStyle = "#ffd700";
    ctx.font = `bold 8px ${GAME_FONT}`;
    ctx.fillText(lobby.lobbyCode, centerX, topY);
    topY += 14;

    // Status / error
    if (lobby.error) {
      ctx.fillStyle = "#ff6b6b";
      ctx.font = mFontPlain(5);
      ctx.fillText(lobby.error, centerX, topY);
      topY += 12;
    }

    topY += 4;

    // ── Character portraits (bottom) ──
    const bobOffset = Math.sin(timeSec * 1.8) * bobAmp;
    const localChar = localPlayer ? (getCharacterById(localPlayer.equippedSkin) ?? char) : char;
    const localX = -bigSize / 2 + 35;
    const localBaseY = ch - bigSize * 1.0;
    const localY = localBaseY + bobOffset;
    deps.drawCharacterPortrait(localChar.imagePath, localX, localY, bigSize, bigSize);

    // Opponent — right half, mirrored
    const opBob = Math.sin(timeSec * 1.8 + Math.PI) * bobAmp;
    const opX = CANVAS_WIDTH - (-bigSize / 2 + 35) - bigSize;
    const opBaseY = ch - bigSize * 1.0;
    const opY = opBaseY + opBob;

    if (opponent != null) {
      const opChar = getCharacterById(opponent.equippedSkin) ?? char;
      ctx.save();
      ctx.translate(CANVAS_WIDTH, 0);
      ctx.scale(-1, 1);
      if (opponent.equippedSkin === "rafsby" && deps.rafsbyPortrait) {
        ctx.drawImage(deps.rafsbyPortrait, CANVAS_WIDTH - opX - bigSize, opY, bigSize, bigSize);
      } else {
        deps.drawCharacterPortrait(opChar.imagePath, CANVAS_WIDTH - opX - bigSize, opY, bigSize, bigSize);
      }
      ctx.restore();
    } else {
      deps.drawHourglass(opX + bigSize / 2, opY + bigSize * 0.3, 48, "#ff9a9a");
    }

    // VS divider
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold 14px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(255,255,255,0.4)";
    ctx.shadowBlur = 8;
    ctx.fillText("VS", centerX, ch * 0.55);
    ctx.shadowBlur = 0;

    // ── Labels above each character: LVL → USERNAME → READY ──
    const readyBtnW = 80;
    const readyBtnH = 24;
    const nameXLocal = localX + bigSize / 2 + 20;
    let labelY = localBaseY - 58;

    // Local level
    ctx.fillStyle = "#ffd700";
    ctx.font = mFont("bold", 5);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(formatDerivedAccountLevel(localPlayer?.level ?? model.level), nameXLocal, labelY);
    labelY += 12;

    // Local username
    ctx.fillStyle = "#d4e7ff";
    ctx.font = mFont("bold", 7);
    ctx.fillText((localPlayer?.displayName ?? model.username ?? "PLAYER").toUpperCase().slice(0, 16), nameXLocal, labelY);
    labelY += 14;

    // Local ready toggle (tappable, with scale animation)
    const localReadyX = nameXLocal - readyBtnW / 2;
    const versusReady: Rect = { x: localReadyX, y: labelY, w: readyBtnW, h: readyBtnH };
    const localIsReady = localPlayer?.ready ?? false;
    const localReadyFill = localIsReady ? "rgba(20,60,40,0.85)" : "rgba(20,36,54,0.85)";
    const localReadyStroke = localIsReady ? "rgba(80,220,120,0.6)" : "rgba(108,200,255,0.6)";
    const localReadyLabel = localIsReady ? "READY" : "NOT READY";
    roundedRectPath(ctx, versusReady.x, versusReady.y, versusReady.w, versusReady.h, 6);
    ctx.fillStyle = localReadyFill;
    ctx.fill();
    ctx.strokeStyle = localReadyStroke;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "#e9f7ff";
    ctx.font = mFont("bold", 5);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(localReadyLabel, versusReady.x + versusReady.w / 2, versusReady.y + versusReady.h / 2 + 1);

    // Opponent labels + ready indicator
    if (opponent != null) {
      const nameXOp = opX + bigSize / 2 - 20;
      let opLabelY = opBaseY - 58;

      // Opponent level
      ctx.fillStyle = "#ffd700";
      ctx.font = mFont("bold", 5);
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(formatDerivedAccountLevel(opponent.level), nameXOp, opLabelY);
      opLabelY += 12;

      // Opponent username
      ctx.fillStyle = "#ffffff";
      ctx.font = mFont("bold", 7);
      ctx.fillText(opponent.displayName.toUpperCase().slice(0, 16), nameXOp, opLabelY);
      opLabelY += 14;

      // Opponent ready indicator (not tappable)
      const opReadyX = nameXOp - readyBtnW / 2;
      const opIsReady = opponent.ready;
      const opReadyFill = opIsReady ? "rgba(20,60,40,0.85)" : "rgba(20,36,54,0.85)";
      const opReadyStroke = opIsReady ? "rgba(80,220,120,0.6)" : "rgba(108,200,255,0.6)";
      const opReadyLabel = opIsReady ? "READY" : "NOT READY";
      roundedRectPath(ctx, opReadyX, opLabelY, readyBtnW, readyBtnH, 6);
      ctx.fillStyle = opReadyFill;
      ctx.fill();
      ctx.strokeStyle = opReadyStroke;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = "#e9f7ff";
      ctx.font = mFont("bold", 5);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(opReadyLabel, opReadyX + readyBtnW / 2, opLabelY + readyBtnH / 2 + 1);
    }

    // ── Speech bubbles (30% lower, 30% bigger) ──
    const msgs = lobby.chatMessages;
    if (msgs.length > 0) {
      const lastMsg = msgs[msgs.length - 1];
      const isLocal = lastMsg.displayName === localPlayer?.displayName;
      const bubbleY = ch * 0.34;
      const bubbleText = lastMsg.message.slice(0, 30);
      ctx.font = mFontPlain(5.2);
      const textW = ctx.measureText(bubbleText).width;
      const padX = 10;
      const padY = 7;
      const bw = textW + padX * 2;
      const bh = 18 + padY * 2;
      const bx = isLocal ? centerX - bw - 6 : centerX + 6;
      const by = bubbleY - bh / 2;

      // Bubble bg (white)
      ctx.fillStyle = "#ffffff";
      roundedRectPath(ctx, bx, by, bw, bh, 8);
      ctx.fill();

      // Tail triangle
      const tailX = isLocal ? bx + 14 : bx + bw - 14;
      const tailY = by + bh;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.moveTo(tailX - 5, tailY);
      ctx.lineTo(tailX, tailY + 8);
      ctx.lineTo(tailX + 5, tailY);
      ctx.closePath();
      ctx.fill();

      // Text (black)
      ctx.fillStyle = "#111111";
      ctx.font = mFontPlain(5.2);
      ctx.textAlign = isLocal ? "left" : "right";
      ctx.textBaseline = "middle";
      ctx.fillText(bubbleText, isLocal ? bx + padX : bx + bw - padX, bubbleY);
    }

    // ── Send message bar (bottom, clean style) ──
    const sendBarH = menuBtnH() + 4;
    const sendBarY = ch - sendBarH - 24;
    const sendBtnW = 52;
    const inputGap = 6;
    const lobbyChatInput: Rect = { x: menuCols.fullX, y: sendBarY, w: menuCols.fullW - sendBtnW - inputGap, h: sendBarH };
    roundedRectPath(ctx, lobbyChatInput.x, lobbyChatInput.y, lobbyChatInput.w, lobbyChatInput.h, 6);
    ctx.fillStyle = panelFill;
    ctx.fill();
    ctx.strokeStyle = panelStroke;
    ctx.lineWidth = 1.5;
    ctx.stroke();


    const versusChat: Rect = { x: menuCols.fullX + menuCols.fullW - sendBtnW, y: sendBarY, w: sendBtnW, h: sendBarH };
    roundedRectPath(ctx, versusChat.x, versusChat.y, versusChat.w, versusChat.h, 6);
    ctx.fillStyle = panelFill;
    ctx.fill();
    ctx.strokeStyle = panelStroke;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "#e9f7ff";
    ctx.font = mFont("bold", 5);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("SEND", versusChat.x + versusChat.w / 2, versusChat.y + versusChat.h / 2 + 1);

    return drawLobbyClose({
      versusReady,
      versusChat,
      lobbyChatInput,
    });
  } else {
    // Creating lobby — show hourglass like battle search
    const ch = deps.canvasHeight;
    const char = model.equippedCharacter;
    const bigSize = CANVAS_WIDTH * 1.2;
    const bobOffset = Math.sin(deps.nowSec() * 1.8) * 4;
    const localX = -bigSize / 2 + 35;
    const localY = ch - bigSize * 1.0 + bobOffset;
    deps.drawCharacterPortrait(char.imagePath, localX, localY, bigSize, bigSize);

    deps.drawHourglass(CANVAS_WIDTH - (-bigSize / 2 + 35) - bigSize / 2, ch * 0.4, 48, "#ff9a9a");

    ctx.fillStyle = "#6a8ab8";
    ctx.font = mFontPlain(8);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("CREATING LOBBY...", centerX, 160);

    return drawLobbyClose({});
  }
}
